import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { keycloak } from '../../services/keycloak';
import { currentTenantId } from '../../services/api';

const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const requestArgs = typeof args === 'string' ? { url: args } : args;

  const baseQuery = fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:7006/api/v1',
    // Per-call override (e.g. purchase-bill extract/upload, which can take
    // well over a minute for vision inference on a dense multi-page bill) —
    // pass `timeout` in the request args to use it instead of this default.
    timeout: (requestArgs as FetchArgs).timeout ?? 10000,
    prepareHeaders: async (headers) => {
      if (keycloak.authenticated) {
        try {
          await keycloak.updateToken(30);
        } catch {
          // token refresh failed; request will proceed unauthenticated
        }
        if (keycloak.token) {
          headers.set('Authorization', `Bearer ${keycloak.token}`);
        }
      }
      if (currentTenantId) headers.set('X-Tenant-Id', currentTenantId);
      return headers;
    },
  });

  const result = await baseQuery(requestArgs, api, extraOptions);

  if ((result.error as FetchBaseQueryError)?.status === 401) {
    try {
      await keycloak.updateToken(30);
      return baseQuery(requestArgs, api, extraOptions);
    } catch {
      keycloak.login();
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'Article',
    'ArticleSupplier',
    'Warehouse',
    'Location',
    'Address',
    'Supplier',
    'Inventory',
    'InventoryTransaction',
    'Imports',
    'ImportConfigs',
    'dynamic-tag',
  ],
  endpoints: () => ({}),
});

export default api;
